"use client";

import {
  Check,
  Pause,
  Play,
  Square,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./how-it-works.module.css";
import type { EmbeddedStageProps } from "./stage-embed";

const KNOWLEDGE_INPUTS = [
  "10+ years in Equity Research",
  "Academic literature",
  "Conversations with portfolio managers",
  "Intuition",
  "Understanding emotional biases",
  "Continuous learning",
  "Investment and business literature",
  "Interdisciplinary education",
] as const;

const BRAIN_POSITIONS = [
  { x: 224, y: 112 },
  { x: 309, y: 88 },
  { x: 397, y: 122 },
  { x: 176, y: 202 },
  { x: 291, y: 188 },
  { x: 423, y: 207 },
  { x: 241, y: 286 },
  { x: 363, y: 291 },
] as const;

const LATTICE_NODES = [
  { x: 260, y: 67, code: "EXP" },
  { x: 360, y: 67, code: "ACA" },
  { x: 210, y: 155, code: "PM" },
  { x: 310, y: 155, code: "INT" },
  { x: 410, y: 155, code: "BIAS" },
  { x: 260, y: 243, code: "LEARN" },
  { x: 360, y: 243, code: "BOOKS" },
  { x: 310, y: 331, code: "MULTI" },
] as const;

const LATTICE_EDGES = [
  [0, 1],
  [0, 2],
  [0, 3],
  [1, 3],
  [1, 4],
  [2, 3],
  [2, 5],
  [3, 4],
  [3, 5],
  [3, 6],
  [4, 6],
  [5, 6],
  [5, 7],
  [6, 7],
] as const;

const STATUS_LABELS = [
  "FORMING THE ANALYST'S THINKING",
  "QUANTITATIVE MODEL OF AN ANALYST'S THINKING",
] as const;
const SPEEDS = [0.5, 1, 1.5, 2] as const;

const INPUT_START_MS = 350;
const INPUT_STEP_MS = 700;
const PHASE_KNOWLEDGE_COLLAPSE_MS = 8000;
const PHASE_LATTICE_MS = 8900;
const PHASE_FINAL_MS = 11000;
export const ANALYST_MIND_DURATION_MS = 12500;
const DURATION_MS = ANALYST_MIND_DURATION_MS;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function stateFromTime(ms: number) {
  const t = Math.max(0, Math.min(DURATION_MS, ms));
  let activeInput = -1;
  if (t >= INPUT_START_MS) {
    activeInput = Math.min(
      KNOWLEDGE_INPUTS.length - 1,
      Math.floor((t - INPUT_START_MS) / INPUT_STEP_MS),
    );
  }

  let phase = 0;
  if (t > 0) phase = 1;
  if (t >= PHASE_LATTICE_MS) phase = 2;
  if (t >= PHASE_FINAL_MS) phase = 3;

  return { t, activeInput, phase };
}

function formatClock(ms: number) {
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function AnalystMindStage({
  embedded = false,
  elapsed: elapsedProp,
  scrubbing: scrubbingProp,
  statusLabels,
}: EmbeddedStageProps & { statusLabels?: readonly [string, string] }) {
  const controlled = embedded && typeof elapsedProp === "number";
  const [elapsedState, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);
  const [scrubbingState, setScrubbing] = useState(false);
  const elapsed = controlled ? elapsedProp : elapsedState;
  const scrubbing = controlled ? Boolean(scrubbingProp) : scrubbingState;
  const stageRef = useRef<HTMLElement | null>(null);
  const hasPlayedRef = useRef(false);
  const lastTsRef = useRef(0);
  const elapsedRef = useRef(0);
  const speedRef = useRef<(typeof SPEEDS)[number]>(1);
  const playingBeforeScrubRef = useRef(false);

  elapsedRef.current = elapsed;
  speedRef.current = speed;

  const { activeInput, phase } = useMemo(() => stateFromTime(elapsed), [elapsed]);

  const startFrom = useCallback((ms: number) => {
    if (prefersReducedMotion()) {
      setElapsed(DURATION_MS);
      setPlaying(false);
      return;
    }
    setElapsed(ms);
    elapsedRef.current = ms;
    lastTsRef.current = 0;
    setPlaying(true);
  }, []);

  const togglePlay = useCallback(() => {
    if (prefersReducedMotion()) {
      setElapsed(DURATION_MS);
      setPlaying(false);
      return;
    }
    if (elapsedRef.current >= DURATION_MS) {
      startFrom(0);
      return;
    }
    setPlaying((current) => !current);
  }, [startFrom]);

  const stop = useCallback(() => {
    setPlaying(false);
    lastTsRef.current = 0;
    setElapsed(0);
    elapsedRef.current = 0;
  }, []);

  useEffect(() => {
    if (controlled || !playing || scrubbing) {
      lastTsRef.current = 0;
      return;
    }

    let frame = 0;
    const loop = (now: number) => {
      if (!lastTsRef.current) lastTsRef.current = now;
      const next = Math.min(
        DURATION_MS,
        elapsedRef.current + (now - lastTsRef.current) * speedRef.current,
      );
      lastTsRef.current = now;
      elapsedRef.current = next;
      setElapsed(next);
      if (next >= DURATION_MS) {
        setPlaying(false);
        return;
      }
      frame = requestAnimationFrame(loop);
    };

    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [controlled, playing, scrubbing]);

  useEffect(() => {
    if (embedded) return;

    const target = stageRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasPlayedRef.current) {
          hasPlayedRef.current = true;
          startFrom(0);
        }
      },
      { threshold: 0.22 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [embedded, startFrom]);

  const inLattice = phase >= 2;
  const finalModel = phase >= 3;
  const knowledgeCollapsed = elapsed >= PHASE_KNOWLEDGE_COLLAPSE_MS;
  const statusPhase = finalModel ? 1 : 0;
  const labels = statusLabels ?? STATUS_LABELS;
  const progress = elapsed / DURATION_MS;

  const visual = (
        <div
          className={`${styles.mindBody} ${
            knowledgeCollapsed ? styles.mindBodyKnowledgeCollapsed : ""
          }`}
        >
          <div className={styles.knowledgePanel}>
            <div className={styles.mindColumnLabel}>KNOWLEDGE INPUTS</div>
            <div className={styles.knowledgeList}>
              {KNOWLEDGE_INPUTS.map((item, index) => {
                const active = activeInput >= index;
                return (
                  <div
                    className={`${styles.knowledgeItem} ${
                      active ? styles.knowledgeItemActive : ""
                    }`}
                    key={item}
                  >
                    <span className={styles.knowledgeIndex}>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className={styles.knowledgeText}>{item}</span>
                    <Check size={16} strokeWidth={2.2} />
                  </div>
                );
              })}
            </div>
          </div>

          <div
            className={`${styles.mindVisual} ${
              finalModel ? styles.mindVisualFinal : ""
            }`}
          >
            <div
              className={styles.mindVisualLabel}
              aria-label={labels[statusPhase]}
            >
              <span className={styles.mindStatusText} aria-hidden="true">
                {labels.map((label, index) => (
                  <span
                    className={`${statusPhase === index ? styles.mindStatusTextActive : ""} ${
                      index < statusPhase ? styles.mindStatusTextPast : ""
                    }`}
                    key={label}
                  >
                    {label}
                  </span>
                ))}
              </span>
            </div>

            <svg
              className={styles.brainSvg}
              viewBox="0 0 620 410"
              role="img"
              aria-label="Knowledge inputs forming a quantitative lattice model"
            >
              <g
                className={`${styles.brainDrawing} ${
                  inLattice ? styles.brainDrawingHidden : ""
                }`}
              >
                <path
                  className={styles.brainOutline}
                  d="M304 46C263 20 212 35 190 75C145 70 111 102 113 144C76 164 68 215 99 244C82 286 111 329 154 331C171 372 219 381 251 353C279 383 323 373 337 338C374 367 424 351 436 309C479 306 503 261 483 223C512 187 491 137 451 127C443 85 399 62 362 79C350 56 327 44 304 46Z"
                />
                <path className={styles.brainDivision} d="M304 51C280 91 311 119 289 151C270 178 302 204 286 234C270 264 302 292 283 336" />
                <path className={styles.brainFold} d="M190 76C181 109 210 125 200 154C190 180 156 175 145 201" />
                <path className={styles.brainFold} d="M114 145C153 141 166 165 156 194C147 220 119 228 101 245" />
                <path className={styles.brainFold} d="M153 331C167 300 197 299 219 315C239 330 236 349 251 353" />
                <path className={styles.brainFold} d="M362 79C342 107 365 130 395 128C426 126 439 145 451 127" />
                <path className={styles.brainFold} d="M483 223C447 214 421 232 426 264C430 287 449 298 436 309" />
                <path className={styles.brainFold} d="M337 338C353 305 382 296 403 312C420 325 421 340 420 345" />
                <path className={styles.brainFold} d="M215 113C244 121 252 143 238 166C226 184 203 185 181 180" />
                <path className={styles.brainFold} d="M332 157C353 137 386 143 395 169C403 194 383 212 356 210" />
                <path className={styles.brainFold} d="M195 250C216 228 250 235 259 261C266 284 245 302 222 298" />
                <path className={styles.brainStem} d="M284 346C289 378 303 394 328 397C321 375 329 353 344 331" />
              </g>

              <g
                className={`${styles.latticeLines} ${
                  inLattice ? styles.latticeLinesActive : ""
                }`}
              >
                {LATTICE_EDGES.map(([from, to]) => (
                  <line
                    key={`${from}-${to}`}
                    x1={LATTICE_NODES[from].x}
                    y1={LATTICE_NODES[from].y}
                    x2={LATTICE_NODES[to].x}
                    y2={LATTICE_NODES[to].y}
                    pathLength="1"
                  />
                ))}
              </g>

              {LATTICE_NODES.map((node, index) => {
                const position = inLattice ? node : BRAIN_POSITIONS[index];
                const active = activeInput >= index;

                return (
                  <g
                    className={`${styles.mindNode} ${
                      active ? styles.mindNodeActive : ""
                    } ${inLattice ? styles.mindNodeLattice : ""} ${
                      finalModel ? styles.mindNodeFinal : ""
                    }`}
                    key={node.code}
                    style={{
                      transform: `translate(${position.x}px, ${position.y}px)`,
                      transitionDelay: "0ms",
                    }}
                  >
                    <circle className={styles.mindNodeHalo} r="35" />
                    <circle className={styles.mindNodeCircle} r="27" />
                    <text className={styles.mindNodeValue} textAnchor="middle" y="4">
                      {node.code}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
  );

  if (embedded) return visual;

  return (
    <section className={styles.mindSection} id="analyst-mind" ref={stageRef}>
      <div className={styles.sectionHeading}>
        <div>
          <h2>The analyst&apos;s mind</h2>
        </div>
      </div>

      <div className={`${styles.mindFrame} ${scrubbing ? styles.mindFrameInstant : ""}`}>
        {visual}

        <div className={styles.mindTransport}>
          <div className={styles.transportButtons}>
            <button
              type="button"
              className={styles.transportButton}
              onClick={togglePlay}
              aria-label={playing ? "Pause animation" : "Play animation"}
            >
              {playing ? <Pause size={16} strokeWidth={1.8} /> : <Play size={16} strokeWidth={1.8} />}
            </button>
            <button
              type="button"
              className={styles.transportButton}
              onClick={stop}
              aria-label="Stop and reset animation"
            >
              <Square size={13} strokeWidth={2} />
            </button>
          </div>

          <label className={styles.transportScrub}>
            <span className={styles.srStatus}>Animation timeline</span>
            <input
              type="range"
              min={0}
              max={DURATION_MS}
              step={50}
              value={elapsed}
              aria-valuetext={`${formatClock(elapsed)} of ${formatClock(DURATION_MS)}`}
              onPointerDown={() => {
                playingBeforeScrubRef.current = playing;
                setPlaying(false);
                setScrubbing(true);
              }}
              onPointerUp={() => {
                setScrubbing(false);
                if (playingBeforeScrubRef.current && elapsedRef.current < DURATION_MS) {
                  setPlaying(true);
                }
              }}
              onChange={(event) => {
                const next = Number(event.target.value);
                elapsedRef.current = next;
                setElapsed(next);
              }}
            />
            <span
              className={styles.transportFill}
              style={{ width: `${progress * 100}%` }}
              aria-hidden="true"
            />
          </label>

          <span className={styles.transportTime}>
            {formatClock(elapsed)} / {formatClock(DURATION_MS)}
          </span>

          <div className={styles.transportSpeeds} role="group" aria-label="Playback speed">
            {SPEEDS.map((value) => (
              <button
                type="button"
                key={value}
                className={`${styles.transportSpeed} ${
                  speed === value ? styles.transportSpeedActive : ""
                }`}
                onClick={() => setSpeed(value)}
              >
                {value}x
              </button>
            ))}
          </div>
        </div>
      </div>

      <span className={styles.srStatus} aria-live="polite">
        {playing
          ? `Playing at ${speed}x`
          : elapsed === 0
            ? "Animation stopped"
            : elapsed >= DURATION_MS
              ? "Animation complete"
              : "Animation paused"}
        {phase > 0 ? `. ${labels[statusPhase]}` : ""}
      </span>
    </section>
  );
}
